const BLOCKED_MARKET_SCAN_SOURCE_IDS = new Set([
  "automation-outlet",
  "oemsecrets",
]);

const BLOCKED_MARKET_SCAN_HOSTS = new Set([
  "automation-outlet.com",
  "automation-outlet.co.uk",
  "automationoutlet.com",
  "automationoutlet.co.uk",
  "oemsecrets.com",
  "oemsecretsapi.com",
]);

// These legacy adapters describe an OEM product, not a distributor or
// marketplace offer. Their records now live in the manufacturer catalogue.
const MANUFACTURER_CATALOGUE_SOURCE_IDS = new Set([
  "netilion",
  "schneider",
  "siemens",
]);

function normalizedSourceId(value: string | null | undefined): string {
  return (value ?? "")
    .trim()
    .toLocaleLowerCase("en-GB")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function normalizedSourceName(value: string | null | undefined): string {
  return (value ?? "").toLocaleLowerCase("en-GB").replace(/[^a-z0-9]+/g, "");
}

/** Sources intentionally excluded from every customer-facing Market Scan path. */
export function isBlockedMarketScanSource(
  sourceId: string | null | undefined,
  sourceName?: string | null,
): boolean {
  const id = normalizedSourceId(sourceId);
  const name = normalizedSourceName(sourceName);
  return [...BLOCKED_MARKET_SCAN_SOURCE_IDS].some((blocked) => (
    id === blocked
    || id.startsWith(`${blocked}-`)
    || id.endsWith(`-${blocked}`)
    || id.includes(`-${blocked}-`)
  ))
    || /^(?:the)?automationoutlet(?:limited|ltd|uk|co|company)*$/.test(name)
    || /^(?:the)?oemsecrets(?:limited|ltd|uk|co|company)*$/.test(name);
}

/** Domain-level backstop used by crawlers and the signed outbound redirect. */
export function isBlockedMarketScanDestination(value: string | URL | null | undefined): boolean {
  if (!value) return false;
  try {
    const url = value instanceof URL ? value : new URL(value);
    const hostname = url.hostname.toLocaleLowerCase("en-GB").replace(/\.$/, "");
    return [...BLOCKED_MARKET_SCAN_HOSTS].some((blocked) => (
      hostname === blocked || hostname.endsWith(`.${blocked}`)
    ));
  } catch {
    return false;
  }
}

/**
 * Browser-safe form of a manufacturer-owned reference already accepted by the
 * catalogue ingestion policy. The server remains responsible for enforcing
 * each connector's exact origin/path allowlist; this is a final UI backstop.
 */
export function safeOfficialManufacturerReference(
  value: string | URL | null | undefined,
): string | null {
  if (!value) return null;
  try {
    const url = value instanceof URL ? new URL(value.toString()) : new URL(value);
    if (url.protocol !== "https:" || url.username || url.password || isBlockedMarketScanDestination(url)) {
      return null;
    }
    return url.toString();
  } catch {
    return null;
  }
}

/** Compatibility policy for callers that have a source identity and destination URL. */
export function isExcludedMarketScanSource(input: {
  sourceId?: string | null;
  sourceName?: string | null;
  url?: string | URL | null;
}): boolean {
  return isBlockedMarketScanSource(input.sourceId, input.sourceName)
    || isBlockedMarketScanDestination(input.url);
}

/** Prevent OEM catalogue records from leaking into supplier price/stock UI. */
export function isManufacturerCatalogueSource(
  sourceId: string | null | undefined,
): boolean {
  return MANUFACTURER_CATALOGUE_SOURCE_IDS.has(normalizedSourceId(sourceId));
}
